import React, { useState, useContext } from 'react';
import { Button, Input, Label } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import PsychometricService from '../../services/PsychometricService';
import Message from '../general/Message';
import { ThemeContext } from "../../contexts/ThemeContext";
import PsychometricTestMainNavbar from './PsychometricTestMainNavbar';
import ErrorBoundary from "../general/ErrorBoundary";

const PsychometricForgotPassword = () => {
    const history = useHistory();
    const { theme } = useContext(ThemeContext);
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [userType, setUserType] = useState('');
    const [message, setMessage] = useState({ msgBody: '', msgError: false });
    const [isLoading, setIsLoading] = useState(false);

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await PsychometricService.requestPasswordReset(email, userType);
            setMessage({ msgBody: response.message.msgBody, msgError: false });
            setStep(2);
        } catch (error) {
            setMessage({ msgBody: error.message.msgBody, msgError: true });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setMessage({ msgBody: "Passwords do not match", msgError: true });
            return;
        }

        setIsLoading(true);
        try {
            const response = await PsychometricService.verifyResetToken(
                email,
                resetToken,
                newPassword,
                userType
            );
            setMessage({ msgBody: response.message.msgBody, msgError: false });
            setTimeout(() => {
                history.push('/');
            }, 2000);
        } catch (error) {
            setMessage({ msgBody: error.message.msgBody, msgError: true });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <ErrorBoundary errorAt="At Psychometric Forgot Password Navbar">
                <PsychometricTestMainNavbar />
            </ErrorBoundary>

            <div className="text-center mt-4">
                <h1 style={theme.headingXXL}>Reset Password</h1>
            </div>

            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-4">
                        <div className="p-3 mt-5 mb-5" style={theme.form}>
                            <ErrorBoundary errorAt="At Forgot Password Message">
                                {message.msgBody && <Message message={message} />}
                            </ErrorBoundary>

                            {step === 1 && (
                                <form onSubmit={handleRequestReset} style={{ paddingLeft: "10%", paddingRight: "10%" }}>
                                    <h2 style={theme.heading}>Request Reset Code</h2>
                                    <div className="mb-3">
                                        <Label style={theme.formBodyText}>User Type</Label>
                                        <div>
                                            <div className="form-check mb-2">
                                                <Input
                                                    type="radio"
                                                    name="userType"
                                                    value="individual"
                                                    onChange={(e) => setUserType(e.target.value)}
                                                    required
                                                    style={theme.formInput}
                                                />
                                                <Label check style={theme.formBodyText}>Individual</Label>
                                            </div>
                                            <div className="form-check">
                                                <Input
                                                    type="radio"
                                                    name="userType"
                                                    value="organization"
                                                    onChange={(e) => setUserType(e.target.value)}
                                                    style={theme.formInput}
                                                />
                                                <Label check style={theme.formBodyText}>Organisation</Label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <Label style={theme.formBodyText}>Email</Label>
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            style={theme.formInput}
                                        />
                                    </div>
                                    <div className="d-flex">
                                        <Button 
                                            type="button"
                                            color="secondary"
                                            onClick={() => history.push('/psychometricManagementPortal')}
                                            style={{ ...theme.formSubmit, marginRight: '10px' }}
                                        >
                                            Back
                                        </Button>
                                        <Button 
                                            type="submit" 
                                            color="primary" 
                                            disabled={isLoading}
                                            style={theme.formSubmit}
                                        >
                                            {isLoading ? (
                                                <span className="spinner-border spinner-border-sm mr-1"></span>
                                            ) : null}
                                            {isLoading ? 'Sending...' : 'Request'}
                                        </Button>
                                    </div>
                                </form>
                            )}

                            {step === 2 && (
                                <form onSubmit={handleResetPassword} style={{ paddingLeft: "10%", paddingRight: "10%" }}>
                                    <h2 style={theme.heading}>Reset Your Password</h2>
                                    <div className="mb-3">
                                        <Label style={theme.formBodyText}>Reset Code</Label>
                                        <Input
                                            type="text"
                                            value={resetToken}
                                            onChange={(e) => setResetToken(e.target.value)}
                                            required
                                            style={theme.formInput}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <Label style={theme.formBodyText}>New Password</Label>
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            style={theme.formInput}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <Label style={theme.formBodyText}>Confirm Password</Label>
                                        <Input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            style={theme.formInput}
                                        />
                                    </div>
                                    <div className="d-grid gap-2">
                                        <Button 
                                            type="submit" 
                                            color="primary" 
                                            disabled={isLoading}
                                            style={theme.formSubmit}
                                        >
                                            {isLoading ? (
                                                <span className="spinner-border spinner-border-sm mr-1"></span>
                                            ) : null}
                                            {isLoading ? 'Resetting...' : 'Reset Password'}
                                        </Button>
                                        <Button
                                            type="button"
                                            color="secondary"
                                            onClick={() => setStep(1)}
                                            style={{ ...theme.formSubmit, marginTop: '10px' }}
                                        >
                                            Back
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PsychometricForgotPassword;